import axios from "axios";
import Cookies from "js-cookie";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import {
  Card as AntCard,
  Tag,
  Typography as AntTypography,
  Tooltip,
  Space,
  Button as AntButton,
  message as antMessage,
  Divider,
  Tabs,
} from "antd";
import {
  CopyOutlined,
  SyncOutlined,
  CarOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  ShoppingOutlined,
  ArrowLeftOutlined,
  ThunderboltOutlined,
  InboxOutlined,
  ShopOutlined,
  SearchOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";

import useDarkModeClass from "../../hooks/useDarkModeClass";
import { refresh } from "../../store/adminSlice";
import createThemedSwal from "../../utils/sweetAlertWithTheme";
import OrderDetailSkeleton from "./OrderDetailSkeleton";
import SearchProduct from "./search-product";
import OrderProductList from "./OrderProductList";
import OrderSummaryCard from "./OrderSummaryCard";
import ShippingPackagesSection from "./ShippingPackagesSection";
import FulfillmentBySeller from "./FulfillmentBySeller";
import { getOrderStatusInfo, getShippingCompanyInfo } from "./orderConstants";
import { useTranslation } from "react-i18next";

const TabSetOrder = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shippingCodes, setShippingCodes] = useState("");
  const [isRotating, setIsRotating] = useState(false);
  // Add order summary state
  const [orderSummary, setOrderSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [autoOrderLoading, setAutoOrderLoading] = useState(false);
  const isDarkMode = useDarkModeClass();
  const themedSwal = createThemedSwal(isDarkMode);

  const [form, setForm] = useState({
    coupon: null,
    email: "",
    phone1: "",
    phone2: "",
    city: "",
    country: "",
    state: "",
    address: "",
    postal_code: "",
    gps: "",
    payment_method: "",
    ref_identifier: "",
    ref_link: "",
    tax: 0.0,
    status: "",
    delivery_charge: "0.00",
    cash_on_delivery_amount: "0.00",
    is_paid: false,
    total: 0.0,
    shipping_company: "",
    public_tracking_code: "",
  });

  const getOrder = useCallback(async () => {
    setLoading(true);
    await axios
      .get(`/order/api/v1/orders/admin/${props.id}/`)
      .then((res) => {
        setForm(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response?.status === 401 && Cookies.get("logss")) {
          dispatch(refresh()).then(() => getOrder());
          setLoading(false);
        } else if (!Cookies.get("logss")) {
          navigate("/");
        } else {
          toast.error(t("orderDetails.checkValuesAgain", "please check the values again"));
          setErrors(err.response.data);
          setLoading(false);
        }
      });
  }, [dispatch, navigate, props.id]);

  // Fetch order summary data
  const getOrderSummary = useCallback(async () => {
    if (!props.id) return;

    setSummaryLoading(true);
    try {
      const response = await axios.get(
        `/order/api/v1/order-summary/${props.id}/`,
      );
      setOrderSummary(response.data);
      setSummaryLoading(false);
    } catch (error) {
      if (error.response?.status === 401 && Cookies.get("logss")) {
        dispatch(refresh()).then(() => getOrderSummary());
        setSummaryLoading(false);
      } else if (!Cookies.get("logss")) {
        navigate("/");
      } else {
        toast.error(t("orderDetails.loadSummaryFailed", "Could not load order summary details"));
        setSummaryLoading(false);
      }
    }
  }, [props.id, dispatch, navigate]);

  useEffect(() => {
    if (props.id) {
      getOrder();
      getOrderSummary();
    }
  }, [dispatch, getOrder, getOrderSummary, navigate, props.id]);

  const handelForm = async () => {
    setErrors({});
    setLoading(true);

    await axios
      .patch(`/order/api/v1/orders/admin/${props.id}/`, form)
      .then((res) => {
        setForm({
          coupon: null,
          email: "",
          phone1: "",
          phone2: "",
          city: "",
          country: "",
          state: "",
          address: "",
          postal_code: "",
          gps: "",
          payment_method: "",
          ref_identifier: "",
          ref_link: "",
          tax: 0.0,
          status: "",
          delivery_charge: "0.00",
          cash_on_delivery_amount: "0.00",
          is_paid: false,
          total: 0.0,
        });

        navigate("/orders/admin/", {
          state: {
            message: `Category ${res.data.name} has been updated successfully`,
          },
        });
      })
      .catch((err) => {
        if (err.response?.status === 401 && Cookies.get("logss")) {
          dispatch(refresh()).then(() => handelForm());
          setLoading(false);
        } else if (!Cookies.get("logss")) {
          navigate("/");
        } else {
          toast.error("please check the values again");
          setErrors(err.response.data);
          setLoading(false);
        }
      });
  };

  const handleAutoOrder = async (id) => {
    themedSwal
      .fire({
        title: t("orderDetails.sendOrderConfirm", "Do you want to send the order?"),
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: t("orderDetails.send", "Send"),
        denyButtonText: t("orderDetails.dontSend", "Don't send"),
      })
      .then(async (result) => {
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) {
          setAutoOrderLoading(true);
          try {
            const { data } = await axios.post("/alibaba/api/v1/crossOrder/", {
              wege_order_id: id,
            });
            themedSwal.fire({
              icon: data?.failed_orders ? "error" : "success",
              title: data?.failed_orders ? "Oops..." : "success",
              text: data?.failed_orders
                ? data.failed_orders[0]?.message
                : data.message,
            });
            await getOrder();
          } catch (err) {
            themedSwal.fire({
              icon: "error",
              title: "Oops...",
              text: err?.response?.data?.message || err.message,
            });
          } finally {
            setAutoOrderLoading(false);
          }
        } else if (result.isDenied) {
          Swal.fire(t("orderDetails.orderNotSent", "Order did not send"), "", "info");
        }
      });
  };

  const orderLogisticsDetails = async (id) => {
    themedSwal
      .fire({
        title: "Do you want to send the order?",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: "Send",
        denyButtonText: `Don't send`,
      })
      .then(async (result) => {
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) {
          await axios
            .post("/alibaba/api/v1/orderLogisticsDetails/", { id: id })
            .then(({ data }) => {
              themedSwal.fire({
                icon: data?.failed_orders ? "error" : "success",
                title: data?.failed_orders ? "Oops..." : "success",
                text: data?.failed_orders
                  ? data.failed_orders[0]?.message
                  : data.message,
                // footer: '<a href="#">Why do I have this issue?</a>'
              });
            })
            .then(() => getOrder())
            .catch((err) => {
              themedSwal.fire({
                icon: "error",
                title: "Oops...",
                text: err?.response?.data?.message || err.message,
              });
            });
        } else if (result.isDenied) {
          Swal.fire("Order did not send", "", "info");
        }
      });
  };

  const createOrder = async (id) => {
    themedSwal
      .fire({
        title: "Do you want to send the order?",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: "Send",
        denyButtonText: `Don't send`,
      })
      .then(async (result) => {
        setIsRotating(true);
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) {
          await axios
            .post("/shipping/api/v1/imile/createOrder/", { orderNo: id })
            .then(({ data }) => {
              themedSwal.fire({
                icon: data?.failed_orders ? "error" : "success",
                title: data?.failed_orders ? "Oops..." : "success",
                text: data?.failed_orders
                  ? data.failed_orders[0]?.message
                  : data.message,
                // footer: '<a href="#">Why do I have this issue?</a>'
              });
              setIsRotating(false);
            })
            .then(() => getOrder())
            .catch((err) => {
              themedSwal.fire({
                icon: "error",
                title: "Oops...",
                text:
                  err?.response?.data?.message?.message ||
                  err?.response?.data?.message ||
                  err.message ||
                  "An unexpected error occurred",
              });

              setIsRotating(false);
            });
        } else if (result.isDenied) {
          Swal.fire("Order did not send", "", "info");
          setIsRotating(false);
        }
      });
  };

  const createVnlinOrder = async (id) => {
    themedSwal
      .fire({
        title: "Do you want to send the order?",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: "Send",
        denyButtonText: `Don't send`,
      })
      .then(async (result) => {
        setIsRotating(true);
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) {
          await axios
            .post("/shipping/api/v1/vnlin/orders/", { wege_order_id: id })
            .then(({ data }) => {
              themedSwal.fire({
                icon: data?.failed_orders ? "error" : "success",
                title: data?.failed_orders ? "Oops..." : "success",
                text: data?.failed_orders
                  ? data.failed_orders[0]?.message
                  : data.message,
                // footer: '<a href="#">Why do I have this issue?</a>'
              });
              setIsRotating(false);
            })
            .then(() => getOrder())
            .catch((err) => {
              themedSwal.fire({
                icon: "error",
                title: "Oops...",
                text:
                  err?.response?.data?.message?.message ||
                  err?.response?.data?.message ||
                  err.message ||
                  "An unexpected error occurred",
              });

              setIsRotating(false);
            });
        } else if (result.isDenied) {
          Swal.fire("Order did not send", "", "info");
          setIsRotating(false);
        }
      });
  };

  const generateShipping = async (id) => {
    themedSwal
      .fire({
        title: "Do you want to send the order?",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: "Send",
        denyButtonText: `Don't send`,
      })
      .then(async (result) => {
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) {
          await axios
            .post("/shipping/api/v1/imile/shippingCodes/", { order_id: id })
            .then(({ data }) => {
              setShippingCodes(data.result);

              themedSwal.fire({
                icon: data?.failed_orders ? "error" : "Success",
                title: data?.failed_orders ? "Oops..." : "Success",
                text: data?.failed_orders
                  ? data.failed_orders[0]?.message
                  : data.message,
              });
            })
            .then(() => getOrder())
            .catch((err) => {
              themedSwal.fire({
                icon: "error",
                title: "Oops...",
                text:
                  err?.response?.data?.message ||
                  err?.response?.data?.detail ||
                  err.message,
              });
            });
        } else if (result.isDenied) {
          Swal.fire("Order did not send", "", "info");
        }
      });
  };

  const updateVnlinShipping = async (id) => {
    themedSwal
      .fire({
        title: "Do you want to send the order?",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: "Send",
        denyButtonText: `Don't send`,
      })
      .then(async (result) => {
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) {
          await axios
            .post("/shipping/api/v1/vnlin/update-status/", {
              wege_order_id: id,
            })
            .then(({ data }) => {
              themedSwal.fire({
                icon: data?.failed_orders ? "error" : "Success",
                title: data?.failed_orders ? "Oops..." : "Success",
                text: data?.failed_orders
                  ? data.failed_orders[0]?.message
                  : data.message,
              });
            })
            .then(() => getOrder())
            .catch((err) => {
              themedSwal.fire({
                icon: "error",
                title: "Oops...",
                text:
                  err?.response?.data?.message ||
                  err?.response?.data?.detail ||
                  err.message,
              });
            });
        } else if (result.isDenied) {
          Swal.fire("Order did not send", "", "info");
        }
      });
  };

  const createInternationalOrder = async (id) => {
    themedSwal
      .fire({
        title: "Do you want to send the order?",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: "Send",
        denyButtonText: `Don't send`,
      })
      .then(async (result) => {
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) {
          await axios
            .post("/shipping/api/v1/vnlin/create-international-order/", {
              wege_order_id: id,
            })
            .then(({ data }) => {
              themedSwal.fire({
                icon: data?.failed_orders ? "error" : "Success",
                title: data?.failed_orders ? "Oops..." : "Success",
                text: data?.failed_orders
                  ? data.failed_orders[0]?.message
                  : data.message,
              });
            })
            .then(() => getOrder())
            .catch((err) => {
              themedSwal.fire({
                icon: "error",
                title: "Oops...",
                text:
                  err?.response?.data?.message ||
                  err?.response?.data?.detail ||
                  err.message,
              });
            });
        } else if (result.isDenied) {
          Swal.fire("Order did not send", "", "info");
        }
      });
  };

  const packageVnlinOrder = async (id) => {
    themedSwal
      .fire({
        title: "Do you want to send the order?",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: "Send",
        denyButtonText: `Don't send`,
      })
      .then(async (result) => {
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) {
          await axios
            .post("/shipping/api/v1/vnlin/packages/", { wege_order_id: id })
            .then(({ data }) => {
              themedSwal.fire({
                icon: data?.failed_orders ? "error" : "Success",
                title: data?.failed_orders ? "Oops..." : "Success",
                text: data?.failed_orders
                  ? data.failed_orders[0]?.message
                  : data.message,
              });
            })
            .then(() => getOrder())
            .catch((err) => {
              themedSwal.fire({
                icon: "error",
                title: "Oops...",
                text:
                  err?.response?.data?.message ||
                  err?.response?.data?.detail ||
                  err.message,
              });
            });
        } else if (result.isDenied) {
          Swal.fire("Order did not send", "", "info");
        }
      });
  };

  const customStyle = {
    fontBackground: {
      background: isDarkMode
        ? "linear-gradient(90deg, rgba(40,40,45,1) 35%, rgba(70,71,71,1) 100%)"
        : "linear-gradient(90deg, rgba(232,232,237,1) 35%, rgba(120,121,121,1) 100%)",
    },
    trackingInputBackground: {
      background: "rgb(32 18 147)",
      borderRadius: "0.7rem",
    },
    generateBorderRadius: {
      borderRadius: "2rem",
    },
    trackingInputs: {
      background: "rgb(147 23 23)",
      color: "white",
      borderRadius: "2rem",
    },
  };

  if (loading) {
    return <OrderDetailSkeleton isDarkMode={isDarkMode} />;
  }

  return (
    <Fragment>
      {/* Add rotating animation style */}
      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
      `}</style>

      <div style={{ marginBottom: 20 }}>
        {/* Ant Design Modern Order Header */}
        <AntCard
          style={{
            borderRadius: 12,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            marginBottom: 20,
            border: "1px solid #e8e8e8",
          }}
          bodyStyle={{ padding: "20px 24px" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 16,
              paddingBottom: 16,
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <AntButton
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                style={{ marginRight: 4 }}
              />
              <AntTypography.Title
                level={4}
                style={{ margin: 0, fontWeight: 700 }}
              >
                {t("orderDetails.orderTitle", { id: form?.id, defaultValue: `Order: Wege-${form?.id}` })}
              </AntTypography.Title>
              <Tooltip title={t("orderDetails.copyOrderId", "Copy Order ID")}>
                <AntButton
                  type="text"
                  size="small"
                  icon={<CopyOutlined style={{ color: "#1677ff" }} />}
                  onClick={() => {
                    navigator.clipboard.writeText(String(form?.id));
                    antMessage.success(t("orderDetails.orderIdCopied", "Order ID copied!"));
                  }}
                />
              </Tooltip>

              {/* Status Tag */}
              {(() => {
                const statusInfo = getOrderStatusInfo(form?.status, t);
                return (
                  <Tag
                    color={statusInfo.color}
                    style={{
                      fontSize: 13,
                      padding: "2px 10px",
                      borderRadius: 4,
                      fontWeight: 600,
                    }}
                  >
                    {statusInfo.label}
                  </Tag>
                );
              })()}

              {/* Payment Tag */}
              <Tag
                color={form?.is_paid ? "green" : "volcano"}
                style={{
                  fontSize: 13,
                  padding: "2px 10px",
                  borderRadius: 4,
                  fontWeight: 600,
                }}
              >
                {form?.is_paid ? t("orderDetails.paid", "PAID") : t("orderDetails.notPaid", "NOT PAID")}
              </Tag>

              {/* Shipping Company Tag */}
              {(() => {
                const shippingInfo = getShippingCompanyInfo(
                  form?.shipping_company
                );
                return shippingInfo ? (
                  <Tag
                    color={shippingInfo.color}
                    icon={<CarOutlined />}
                    style={{
                      fontSize: 13,
                      padding: "2px 10px",
                      borderRadius: 4,
                      fontWeight: 600,
                    }}
                  >
                    {shippingInfo.name}
                  </Tag>
                ) : null;
              })()}
            </div>

            {/* Total Amount Badge */}
            <div style={{ textAlign: "right" }}>
              <span style={{ fontSize: 13, color: "#8c8c8c", marginRight: 8 }}>
                {t("orderDetails.totalAmount", "Total Amount:")}
              </span>
              <span
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#1677ff",
                }}
              >
                {Number(form?.total || 0).toFixed(2)} AED
              </span>
            </div>
          </div>

          {/* Details Row */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 24,
              fontSize: 14,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <UserOutlined style={{ color: "#1677ff" }} />
              <span style={{ color: "#8c8c8c" }}>{t("orderDetails.email", "Email:")}</span>
              <span style={{ fontWeight: 500 }}>{form?.email || "N/A"}</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PhoneOutlined style={{ color: "#52c41a" }} />
              <span style={{ color: "#8c8c8c" }}>{t("orderDetails.mobile", "Mobile:")}</span>
              <span style={{ fontWeight: 500 }}>{form?.phone1 || "N/A"}</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <EnvironmentOutlined style={{ color: "#faad14" }} />
              <span style={{ color: "#8c8c8c" }}>{t("orderDetails.address", "Address:")}</span>
              <span style={{ fontWeight: 500 }}>
                {[
                  form?.shipping_address?.address || form?.address,
                  form?.shipping_address?.city || form?.city,
                  form?.shipping_address?.country || form?.country,
                ]
                  .filter(Boolean)
                  .join(", ") || "N/A"}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ShoppingOutlined style={{ color: "#722ed1" }} />
              <span style={{ color: "#8c8c8c" }}>{t("orderDetails.items", "Items:")}</span>
              <span style={{ fontWeight: 600 }}>
                {form?.order_products?.length || 0}
              </span>
            </div>
          </div>
        </AntCard>

        {/* Summary & Shipping Row: 80% summary, 20% shipping card */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "stretch",
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          <div
            style={{
              flex: "1 1 78%",
              minWidth: "300px",
              maxWidth: "80%",
            }}
          >
            <OrderSummaryCard
              orderSummary={orderSummary}
              loading={summaryLoading}
              onRefresh={getOrderSummary}
              isDarkMode={isDarkMode}
              style={{ height: "100%", marginBottom: 0 }}
            />
          </div>
          <div
            style={{
              flex: "1 1 calc(20% - 16px)",
              minWidth: "240px",
            }}
          >
            <AntCard
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CarOutlined style={{ color: "#1677ff" }} />
                  <span>{t("orderDetails.shippingAndTracking", "Shipping & Tracking")}</span>
                </div>
              }
              extra={
                (() => {
                  const shipInfo = getShippingCompanyInfo(
                    form?.shipping_company
                  );
                  return shipInfo ? (
                    <Tag color={shipInfo.color}>{shipInfo.name}</Tag>
                  ) : null;
                })()
              }
              style={{
                borderRadius: 12,
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                border: "1px solid #e8e8e8",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                marginBottom: 0,
              }}
              bodyStyle={{
                padding: "16px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div>
                {(() => {
                  const trackingCode =
                    form.public_tracking_code ||
                    orderSummary?.public_tracking_code;

                  if (!trackingCode) return null;

                  const frontendUrl =
                    process.env.REACT_APP_FRONTEND_URL || "https://dev.wege.ae";
                  const trackingUrl = `${frontendUrl}/en/tracking-order?code=${encodeURIComponent(
                    trackingCode
                  )}`;

                  return (
                    <div style={{ textAlign: "center", marginBottom: 16 }}>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#8c8c8c",
                          marginBottom: 6,
                        }}
                      >
                        {t("orderDetails.publicTracking", "Public Tracking")}
                      </div>
                      <div
                        style={{
                          background: isDarkMode ? "#1f1f1f" : "#f5f5f5",
                          padding: "8px 12px",
                          borderRadius: 6,
                          fontWeight: 600,
                          fontSize: 14,
                          wordBreak: "break-all",
                          border: "1px dashed #d9d9d9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                        }}
                      >
                        <span>{trackingCode}</span>
                        <Tooltip title={t("orderDetails.copyPublicTracking", "Copy Public Tracking")}>
                          <CopyOutlined
                            style={{ cursor: "pointer", color: "#1677ff" }}
                            onClick={() => {
                              navigator.clipboard.writeText(trackingCode);
                              antMessage.success(
                                t("orderDetails.copiedPublicTracking", "Copied public tracking code!")
                              );
                            }}
                          />
                        </Tooltip>
                        <Tooltip title={t("orderDetails.openInFrontend", "Open in Frontend")}>
                          <ExportOutlined
                            style={{ cursor: "pointer", color: "#1677ff" }}
                            onClick={() => window.open(trackingUrl, "_blank")}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  );
                })()}

                <Space
                  direction="vertical"
                  style={{ width: "100%" }}
                  size="middle"
                >
                  <AntButton
                    type="primary"
                    block
                    loading={autoOrderLoading}
                    icon={<ThunderboltOutlined />}
                    style={{
                      background: "#52c41a",
                      borderColor: "#52c41a",
                      fontWeight: 600,
                    }}
                    onClick={() => handleAutoOrder(form.id)}
                  >
                    {t("orderDetails.autoOrder", "Auto Order")}
                  </AntButton>

                  {form.shipping_company === 3 && (
                    <Divider style={{ margin: "4px 0" }} />
                  )}

                  {/* Commented out for now
                  {form.shipping_company === 1 && (
                    <>
                      <AntButton
                        type="primary"
                        block
                        icon={<SyncOutlined spin={isRotating} />}
                        onClick={() => createOrder(form.id)}
                      >
                        Create iMile Order
                      </AntButton>
                      <AntButton
                        type="default"
                        block
                        style={{
                          background: "#52c41a",
                          color: "#fff",
                          borderColor: "#52c41a",
                        }}
                        onClick={() => generateShipping(form.id)}
                      >
                        Generate Shipping
                      </AntButton>
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: 11,
                          color: "#8c8c8c",
                        }}
                      >
                        Generate the tracking number with iMile
                      </div>
                    </>
                  )}
                  */}

                  {/* Commented out for now
                  {form.shipping_company === 2 && (
                    <>
                      <AntButton
                        type="primary"
                        block
                        icon={<SyncOutlined spin={isRotating} />}
                        onClick={() => createVnlinOrder(form.id)}
                      >
                        Create Vnlin Order
                      </AntButton>
                      <AntButton
                        block
                        style={{
                          background: "#52c41a",
                          color: "#fff",
                          borderColor: "#52c41a",
                        }}
                        onClick={() => updateVnlinShipping(form.id)}
                      >
                        Update Vnlin Shipping
                      </AntButton>
                      <AntButton
                        type="primary"
                        ghost
                        block
                        onClick={() => createInternationalOrder(form.id)}
                      >
                        Create International Order
                      </AntButton>
                      <AntButton
                        block
                        onClick={() => packageVnlinOrder(form.id)}
                      >
                        Package Order
                      </AntButton>
                    </>
                  )}
                  */}

                  {form.shipping_company === 3 && (
                    <div style={{ textAlign: "center", padding: "10px 0" }}>
                      <Tag
                        color="orange"
                        style={{ fontSize: 13, padding: "4px 12px" }}
                      >
                        {t("orderDetails.aramexShipment", "Aramex Express Shipment")}
                      </Tag>
                      {orderSummary?.estimated_shipping_cost?.amount && (
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 12,
                            color: "#666",
                          }}
                        >
                          {t("orderDetails.estimatedShipping", "Estimated Shipping:")}{" "}
                          <strong>
                            {orderSummary.estimated_shipping_cost.amount}{" "}
                            {orderSummary.estimated_shipping_cost.currency ||
                              "AED"}
                          </strong>
                        </div>
                      )}
                    </div>
                  )}
                </Space>
              </div>
            </AntCard>
          </div>
        </div>
      </div>

      {/* Lower Details Tabs */}
      <AntCard
        style={{
          borderRadius: 12,
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          border: "1px solid #e8e8e8",
          marginBottom: 24,
        }}
        bodyStyle={{ padding: "16px 20px" }}
      >
        <Tabs
          defaultActiveKey="products"
          items={[
            {
              key: "products",
              label: (
                <span style={{ fontWeight: 600, fontSize: 14 }}>
                  <ShoppingOutlined style={{ marginRight: 6 }} />
                  {t("orderDetails.orderedProducts", "Ordered Products")} ({form?.order_products?.length || 0})
                </span>
              ),
              children: (
                <OrderProductList
                  products={form?.order_products}
                  onProductSelect={(item) => {
                    setSelectedProduct(item);
                    setModalOpen(true);
                  }}
                  isDarkMode={isDarkMode}
                />
              ),
            },
            {
              key: "shipping_packages",
              label: (
                <span style={{ fontWeight: 600, fontSize: 14 }}>
                  <InboxOutlined style={{ marginRight: 6 }} />
                  {t("orderDetails.shippingPackages", "Shipping Packages")} ({form?.shipping_packages?.length || 0})
                </span>
              ),
              children: (
                <ShippingPackagesSection
                  packages={form?.shipping_packages || []}
                  isDarkMode={isDarkMode}
                />
              ),
            },
            {
              key: "fulfillment",
              label: (
                <span style={{ fontWeight: 600, fontSize: 14 }}>
                  <ShopOutlined style={{ marginRight: 6 }} />
                  {t("orderDetails.fulfillmentBySeller", "Fulfillment by Seller")} ({form?.["1688_orders"]?.length || 0})
                </span>
              ),
              children: (
                <FulfillmentBySeller
                  orders={form?.["1688_orders"] || []}
                  isDarkMode={isDarkMode}
                />
              ),
            },
            {
              key: "search_product",
              label: (
                <span style={{ fontWeight: 600, fontSize: 14 }}>
                  <SearchOutlined style={{ marginRight: 6 }} />
                  {t("orderDetails.searchAndAddProduct", "Search & Add Product")}
                </span>
              ),
              children: (
                <div style={{ maxWidth: 960 }}>
                  <SearchProduct
                    wege_order_id={props.id}
                    getOrder={getOrder}
                    isDarkMode={isDarkMode}
                  />
                </div>
              ),
            },
          ]}
        />
      </AntCard>
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {selectedProduct?.variation?.product?.name}
        </ModalHeader>
        <ModalBody>
          <img
            src={`${process.env.REACT_APP_PUBLIC_MEDIA_URL}store/${selectedProduct?.variation?.store}/${selectedProduct?.variation?.images}`}
            alt={selectedProduct?.variation?.product?.name}
            className="img-fluid mb-3"
            style={{
              maxHeight: "700px",
              width: "auto",
              objectFit: "cover",
            }}
          />
          <p>
            <strong>{t("orderDetails.color", "Color")}: </strong>
            {selectedProduct?.variation?.color?.name || "N/A"}
          </p>
          <p>
            <strong>{t("orderDetails.size", "Size")}: </strong>
            {selectedProduct?.variation?.size?.name || "N/A"}
          </p>
          <p>
            <strong>{t("orderDetails.price", "Price")}: </strong>
            {selectedProduct?.variation?.price || "N/A"}
          </p>
          <p>
            <strong>{t("orderDetails.quantity", "Quantity")}: </strong>
            {selectedProduct?.quantity || "N/A"}
          </p>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            {t("orderDetails.close", "Close")}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default TabSetOrder;
